import type { ServerFilter } from '@/components/table/table';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseServerFilterOptions<TData, TResponse> {
  /** Query key base (e.g., ['contacts', 'filter']) */
  queryKey: unknown[];
  /** Function to fetch filtered data */
  queryFn: (params: {
    filter: ServerFilter;
    pagination: { page: number; pageSize: number };
  }) => Promise<TResponse>;
  /** Extract data array from response */
  select: (response: TResponse) => TData[];
  /** Extract pagination info - return next page number or undefined if no more */
  getNextPageParam: (lastPage: TResponse, allPages: TResponse[]) => number | undefined;
  /** Page size */
  pageSize?: number;
  /** Initial filter state (e.g., from URL params) */
  initialFilter?: ServerFilter | null;
  /** Force enable the query even with empty filters (useful for sorting without filtering) */
  forceEnabled?: boolean;
}

export function useServerFilter<TData, TResponse>(
  options: UseServerFilterOptions<TData, TResponse>,
) {
  const {
    queryKey,
    queryFn,
    select,
    getNextPageParam,
    pageSize = 50,
    initialFilter = null,
    forceEnabled = false,
  } = options;

  const [filter, setFilter] = useState<ServerFilter | null>(() => {
    // Initialize with provided filter if it has valid conditions
    if (initialFilter && initialFilter.and.length > 0) {
      return initialFilter;
    }
    // When forceEnabled, allow empty filter (for sorting without filtering)
    if (forceEnabled && initialFilter) {
      return initialFilter;
    }
    return null;
  });

  // Sync filter state when initialFilter changes (e.g., from URL when view is selected)
  useEffect(() => {
    if (initialFilter) {
      if (initialFilter.and.length > 0 || forceEnabled) {
        setFilter(initialFilter);
      } else if (!forceEnabled) {
        setFilter(null);
      }
    } else {
      setFilter(null);
    }
  }, [initialFilter, forceEnabled]);

  // Determine if query should be enabled
  // Use initialFilter as fallback to avoid render cycle delay when forceEnabled changes
  const effectiveFilter = filter ?? initialFilter;
  const hasActiveFilter = effectiveFilter !== null && effectiveFilter.and.length > 0;
  const shouldBeEnabled = hasActiveFilter || (forceEnabled && effectiveFilter !== null);

  // Infinite query for filtered results - only runs when filter is active or forceEnabled
  const filterQuery = useInfiniteQuery({
    queryKey: [...queryKey, effectiveFilter],
    queryFn: ({ pageParam = 0 }) =>
      queryFn({
        filter: effectiveFilter ?? { and: [] },
        pagination: { page: pageParam, pageSize },
      }),
    getNextPageParam,
    initialPageParam: 0,
    enabled: shouldBeEnabled,
    staleTime: 1000 * 10,
    placeholderData: keepPreviousData,
  });

  // Flatten pages into single array
  const data = useMemo(() => {
    if (!filterQuery.data?.pages) {
      return [];
    }
    return filterQuery.data.pages.flatMap(select);
  }, [filterQuery.data?.pages, select]);

  const applyFilter = useCallback(
    (newFilter: ServerFilter) => {
      if (newFilter.and.length === 0 && !forceEnabled) {
        setFilter(null);
      } else {
        setFilter(newFilter);
      }
    },
    [forceEnabled],
  );

  const clearFilter = useCallback(() => {
    setFilter(null);
  }, []);

  return {
    /** Current filter (null = no filter active) */
    filter,
    /** Filtered data (flattened from pages) */
    data,
    /** Whether filter is active (includes forceEnabled for sorting) */
    isActive: shouldBeEnabled,
    /** Initial loading */
    isLoading: filterQuery.isLoading,
    /** Fetching (initial or refetch) */
    isFetching: filterQuery.isFetching,
    /** Fetching next page */
    isFetchingNextPage: filterQuery.isFetchingNextPage,
    /** Whether showing placeholder data from previous query */
    isPlaceholderData: filterQuery.isPlaceholderData,
    /** Has more pages */
    hasNextPage: filterQuery.hasNextPage ?? false,
    /** Fetch next page */
    fetchNextPage: filterQuery.fetchNextPage,
    /** Apply new filter */
    applyFilter,
    /** Clear filter */
    clearFilter,
    /** Query status */
    status: filterQuery.status,
  };
}

export type { ServerFilter };
